/** Folding the group headings on a list of tasks away.
 *
 *  All tasks over a month is a few hundred rows under a hundred-odd company or
 *  person headings, and there was no way to shorten it: every group was always
 *  open, so finding the one client you came for meant scrolling past everybody
 *  else's work.
 *
 *  **On the screens that span weeks, company and person groups start folded** - see
 *  `startFolded` below. A heading keeps the name, the bar and the count, which is
 *  usually the whole answer: "how much is on Deshmukh & Co, and how much of it is
 *  done" needs no rows at all. Opening the one group you came for is a tap;
 *  scrolling past ninety you did not is not.
 *
 *  Date headings never start folded, and that asymmetry is deliberate: a day folded
 *  by default would hide even the company names, and the screen would open showing
 *  nothing but dates - which reads as an empty list rather than a folded one.
 *
 *  Shared by `TaskTable` and `TaskCards` rather than written in each, so the ledger
 *  and the phone view fold the same groups by the same keys. State is per mount and
 *  deliberately not stored: which client you opened an hour ago is not a preference.
 */
import { useCallback, useState } from 'react'

/** Day and heading together, because the same company appears under every date and
 *  folding it on Tuesday must not fold it on Wednesday. `\u0000` cannot occur in a
 *  company or person name, so the pair can never collide - and an empty name is how
 *  a day's own heading is keyed. */
export const groupKey = (date: string, name: string) => `${date}\u0000${name}`

/** A day's own heading, rather than a company's or a person's. */
const isDay = (key: string) => key.endsWith('\u0000')

/** Is this heading folded? The whole rule, as a function of the keys that have been
 *  flipped away from their default.
 *
 *  Exported for its own test: everything else in this file is a hook or a button,
 *  and the part worth pinning - "groups start folded, days never do, a tap means the
 *  opposite of whatever the default was" - is this.
 */
export function isFolded(flipped: ReadonlySet<string>, key: string,
                         startFolded: boolean): boolean {
  const byDefault = startFolded && !isDay(key)
  return flipped.has(key) ? !byDefault : byDefault
}

export interface Collapsed {
  has: (key: string) => boolean
  toggle: (key: string) => void
  collapseAll: (keys: string[]) => void
  expandAll: (keys: string[]) => void
}

/** The set held is of keys that are the OPPOSITE of their default, not of folded
 *  ones. That is what lets "start folded" be a rule rather than a first render that
 *  has to enumerate every group before it has seen the data - and it means a group
 *  that appears later (a new day arrives from a refetch) arrives folded like its
 *  neighbours instead of open. */
export function useCollapsed(startFolded = false): Collapsed {
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(() => new Set())

  // Days are never folded by default - see the note at the top of the file - and
  // on the screens that pass nothing, neither is anything else: Today is one day's
  // work on one screen, and opening it folded would hide the list somebody came to
  // read. `All tasks` and `Carried forward`, which span weeks, ask for folded.
  const shutByDefault = useCallback(
    (k: string) => startFolded && !isDay(k), [startFolded])

  const has = useCallback(
    (k: string) => isFolded(flipped, k, startFolded), [flipped, startFolded])

  const toggle = useCallback((k: string) => setFlipped((prev) => {
    const next = new Set(prev)
    if (!next.delete(k)) next.add(k)
    return next
  }), [])

  // Both of these speak only for the keys they are handed - the group headings on
  // screen. A day's own key is left exactly as it was, so "Collapse all" folds the
  // companies without also folding the days they sit under.
  const collapseAll = useCallback((keys: string[]) => setFlipped((prev) => {
    const next = new Set(prev)
    for (const k of keys) if (shutByDefault(k)) next.delete(k); else next.add(k)
    return next
  }), [shutByDefault])

  const expandAll = useCallback((keys: string[]) => setFlipped((prev) => {
    const next = new Set(prev)
    for (const k of keys) if (shutByDefault(k)) next.add(k); else next.delete(k)
    return next
  }), [shutByDefault])

  return { has, toggle, collapseAll, expandAll }
}

/** The twisty. A rotated triangle rather than two glyphs, so the two states are the
 *  same shape and the eye reads the rotation instead of re-reading a character. */
export function Caret({ open }: { open: boolean }) {
  return (
    <span aria-hidden
          className={`inline-block shrink-0 text-ink-3 text-[9px] leading-none
                      transition-transform ${open ? 'rotate-90' : ''}`}>
      ▶
    </span>
  )
}

/** One tap for the whole list, and the hint that the list is folded at all.
 *
 *  It says what it will DO, so on a page that opens folded the first thing on the
 *  strip is "Expand all" - a control offering to fold an already folded list is how
 *  somebody concludes their tasks are missing. Hidden below two groups, where the
 *  headings themselves do the job.
 */
export function CollapseAll({ collapsed, keys }: {
  collapsed: Collapsed; keys: string[]
}) {
  if (keys.length < 2) return null
  const anyOpen = keys.some((k) => !collapsed.has(k))
  return (
    <button type="button"
            onClick={() => (anyOpen ? collapsed.collapseAll(keys)
                                    : collapsed.expandAll(keys))}
            className="text-[11.5px] text-ink-3 hover:text-ink hover:underline">
      {anyOpen ? 'Collapse all' : 'Expand all'}
    </button>
  )
}
